import { useState } from 'react';
import { Bot, Star, Send, Loader2, Heart, Sparkles, MessageCircle } from 'lucide-react';
import { logger } from '../../lib/logger';

interface FeedbackMessageProps {
  onSubmit: (rating: number, comment?: string) => Promise<void>;
  onDismiss: () => void;
}

export function FeedbackMessage({ onSubmit, onDismiss }: FeedbackMessageProps) {
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedRating, setSubmittedRating] = useState<number | null>(null);

  const handleSubmit = async () => {
    if (rating === 0) return;

    setIsSubmitting(true);
    try {
      await onSubmit(rating, comment);
      setSubmittedRating(rating);
    } catch (error) {
      logger.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get confirmation content based on rating
  const getConfirmation = () => {
    if (!submittedRating) return null;

    // Positive feedback (4-5 stars)
    if (submittedRating >= 4) {
      return {
        icon: <Heart className="w-5 h-5 text-pink-500 fill-pink-500" />,
        bgColor: 'bg-pink-50',
        title: 'Das freut uns sehr!',
        message: 'Danke, dass du dir die Zeit genommen hast. Schön, dass dir MAMAL gefällt!',
        emoji: '🎉',
      };
    }

    // Neutral feedback (3 stars)
    if (submittedRating === 3) {
      return {
        icon: <Sparkles className="w-5 h-5 text-amber-500" />,
        bgColor: 'bg-amber-50',
        title: 'Danke für dein Feedback!',
        message: 'Wir arbeiten ständig daran, MAMAL zu verbessern. Dein Input hilft uns dabei!',
        emoji: '💪',
      };
    }

    // Negative feedback (1-2 stars)
    return {
      icon: <MessageCircle className="w-5 h-5 text-blue-500" />,
      bgColor: 'bg-blue-50',
      title: 'Danke für deine Ehrlichkeit!',
      message: 'Wir nehmen dein Feedback ernst und werden daran arbeiten, MAMAL für dich zu verbessern.',
      emoji: '🙏',
    };
  };

  const confirmation = getConfirmation();

  return (
    <div className="flex gap-3">
      {/* Avatar */}
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-teal-400 to-cyan-500">
        <Bot className="w-4 h-4 text-white" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="inline-block max-w-full rounded-2xl px-4 py-3 bg-gray-100 text-gray-900 rounded-tl-md">
          {submittedRating && confirmation ? (
            <div className="py-1">
              <div className={`flex items-start gap-3 p-3 ${confirmation.bgColor} rounded-xl mb-2`}>
                <div className="flex-shrink-0 mt-0.5">
                  {confirmation.icon}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    {confirmation.title}
                    <span>{confirmation.emoji}</span>
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {confirmation.message}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-0.5 pt-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= submittedRating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
                <span className="ml-2 text-xs text-gray-500">Deine Bewertung</span>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm leading-relaxed mb-4">
                Hey! Ich würde gerne wissen, wie dir <strong>MAMAL</strong> bisher gefällt.
                Dein Feedback hilft uns, die Plattform für dich zu verbessern!
              </p>

              {/* Star Rating */}
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="p-0.5 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-7 h-7 transition-colors ${
                        star <= (hoveredRating || rating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
                {rating > 0 && (
                  <span className="ml-2 text-sm text-gray-500">
                    {rating === 1 && 'Nicht gut'}
                    {rating === 2 && 'Geht so'}
                    {rating === 3 && 'Okay'}
                    {rating === 4 && 'Gut'}
                    {rating === 5 && 'Super!'}
                  </span>
                )}
              </div>

              {/* Comment Input */}
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Was können wir verbessern? (optional)"
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white placeholder-gray-400"
              />

              {/* Actions */}
              <div className="flex items-center justify-between mt-3">
                <button
                  onClick={onDismiss}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Später
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={rating === 0 || isSubmitting}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Senden...
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      Absenden
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Timestamp */}
        <div className="mt-1 text-xs text-gray-400">
          {new Date().toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  );
}
